import type {
  DrawPreview,
  DrawPreviewMatch,
  DrawPreviewParticipant,
  DrawPreviewRound,
  OddPlayerPolicy,
  PairingPriority,
  PlayEvent,
  PlayEventDrawCourtAssignmentInput,
  PlayEventDrawGenerateInput,
  PlayEventDrawManualSlotInput,
  PlayEventParticipant,
  SocialPlayMode,
} from '@court-sync/shared';
import {
  buildRoundRobinDrawPreview,
  buildSingleEliminationDrawPreview,
  buildSocialDrawPreview,
  buildAutoCourtAssignmentsByMatchKey,
  resolveEffectiveSocialPlayMode,
  getDrawPreviewSideIds,
  getSingleEliminationMatchDisplay,
  previewMatchIncludesParticipant,
  resolveSocialRoundMatchCount,
} from '@court-sync/shared';
import type { DrawMatchListItem } from '@/components/play/PlayEventDrawMatchList';

export function participantToDrawPreview(
  participant: PlayEventParticipant
): DrawPreviewParticipant {
  return {
    id: participant.id,
    gender: participant.gender,
    skillLevel: participant.skillLevel,
    playerRating: participant.playerRating,
    dateOfBirth: participant.dateOfBirth,
    prefersSingles: participant.prefersSingles,
    seed: participant.seed,
    displayName: undefined,
  };
}

export function getEligibleDrawParticipants(
  event: PlayEvent,
  participants: PlayEventParticipant[]
): PlayEventParticipant[] {
  if (event.format === 'social_rotate') {
    return participants.filter((participant) => participant.status === 'checked_in');
  }
  return participants.filter(
    (participant) => participant.status === 'registered' || participant.status === 'checked_in'
  );
}

export interface BuildEventDrawPreviewInput {
  event: PlayEvent;
  participants: PlayEventParticipant[];
  allocationMode: 'auto' | 'manual';
  roundCount?: number;
  roundDurationMinutes?: number;
  roundBreakMinutes?: number;
  pairingPriorities?: PairingPriority[];
  socialPlayMode?: SocialPlayMode;
  oddPlayerPolicy?: OddPlayerPolicy;
  /** Increment to produce alternate auto draws in the preview wizard. */
  pairingShuffleSeed?: number;
}

export function buildEventDrawPreview(input: BuildEventDrawPreviewInput): DrawPreview {
  const eligible = getEligibleDrawParticipants(input.event, input.participants);
  const previewParticipants = eligible.map(participantToDrawPreview);

  if (input.event.format === 'social_rotate') {
    const roundCount = input.roundCount ?? input.event.roundCount ?? 4;
    return buildSocialDrawPreview({
      participants: previewParticipants,
      roundCount,
      socialPlayMode:
        input.socialPlayMode ?? resolveEffectiveSocialPlayMode(input.event),
      oddPlayerPolicy: input.oddPlayerPolicy ?? input.event.oddPlayerPolicy ?? 'sit_out',
      side: input.event.side,
      pairingPriorities: input.pairingPriorities ?? input.event.pairingPriorities,
      courtCount: resolveSocialRoundMatchCount(eligible.length),
      allocationMode: input.allocationMode,
      pairingShuffleSeed: input.pairingShuffleSeed,
    });
  }

  if (input.event.format === 'round_robin') {
    return buildRoundRobinDrawPreview({
      participantIds: eligible.map((participant) => participant.id),
      courtCount: resolveSocialRoundMatchCount(eligible.length),
      allocationMode: input.allocationMode,
      pairingShuffleSeed: input.pairingShuffleSeed,
    });
  }

  return buildSingleEliminationDrawPreview({
    participants: previewParticipants,
    allocationMode: input.allocationMode,
    seedingMode: input.allocationMode === 'manual' ? 'manual' : 'auto',
    pairingShuffleSeed: input.pairingShuffleSeed,
  });
}

export function collectManualSlotsFromPreview(preview: DrawPreview): PlayEventDrawManualSlotInput[] {
  const slots: PlayEventDrawManualSlotInput[] = [];

  for (const round of preview.rounds) {
    const sortedMatches = [...round.matches].sort((a, b) => a.matchIndex - b.matchIndex);
    sortedMatches.forEach((match, matchIndex) => {
      if (match.sideAId) {
        slots.push({
          roundNumber: round.roundNumber,
          matchIndex,
          side: 'a',
          participantId: match.sideAId,
        });
      }
      if (match.sideBId) {
        slots.push({
          roundNumber: round.roundNumber,
          matchIndex,
          side: 'b',
          participantId: match.sideBId,
        });
      }
    });
  }

  return slots;
}

export function collectCourtAssignmentsFromPreview(
  courtAssignmentsByKey: Record<string, string | null | undefined>
): PlayEventDrawCourtAssignmentInput[] {
  return Object.entries(courtAssignmentsByKey).map(([key, courtId]) => {
    const [roundNumber, matchIndex] = key.split('-').map(Number);
    return {
      roundNumber,
      matchIndex,
      courtId: courtId ?? null,
    };
  });
}

export function buildAutoCourtAssignmentsForPreview(
  preview: DrawPreview,
  eventCourtIds: string[]
): Record<string, string | null> {
  return buildAutoCourtAssignmentsByMatchKey(
    eventCourtIds,
    preview.rounds.map((round) => ({
      roundNumber: round.roundNumber,
      matches: round.matches.map((match) => ({
        matchIndex: match.matchIndex,
        isBye: match.isBye,
      })),
    }))
  );
}

export function buildDrawGeneratePayload(
  input: BuildEventDrawPreviewInput,
  preview: DrawPreview,
  courtAssignmentsByKey?: Record<string, string | null | undefined>
): PlayEventDrawGenerateInput {
  const payload: PlayEventDrawGenerateInput = {
    allocationMode: input.allocationMode,
  };

  if (input.event.format === 'social_rotate') {
    payload.roundCount = input.roundCount ?? input.event.roundCount ?? 4;
    payload.roundDurationMinutes =
      input.roundDurationMinutes ?? input.event.roundDurationMinutes ?? undefined;
    payload.roundBreakMinutes =
      input.roundBreakMinutes ?? input.event.roundBreakMinutes ?? undefined;
    if (input.allocationMode === 'auto') {
      payload.pairingPriorities = input.pairingPriorities ?? input.event.pairingPriorities;
      payload.socialPlayMode =
        input.socialPlayMode ?? resolveEffectiveSocialPlayMode(input.event);
      payload.oddPlayerPolicy = input.oddPlayerPolicy ?? input.event.oddPlayerPolicy ?? undefined;
    }
  }

  const manualSlots =
    input.allocationMode === 'manual' ? collectManualSlotsFromPreview(preview) : [];
  if (manualSlots.length > 0) {
    payload.manualSlots = manualSlots;
  }

  const courtAssignments = courtAssignmentsByKey
    ? collectCourtAssignmentsFromPreview(courtAssignmentsByKey)
    : [];
  if (courtAssignments.length > 0) {
    payload.courtAssignments = courtAssignments;
  }

  return payload;
}

export function isDoublesFormatPreviewMatch(match: DrawPreviewMatch): boolean {
  return (
    getDrawPreviewSideIds(match, 'a').length >= 2 || getDrawPreviewSideIds(match, 'b').length >= 2
  );
}

function updatePreviewMatchSideIds(
  match: DrawPreviewMatch,
  side: 'a' | 'b',
  participantIds: string[]
): DrawPreviewMatch {
  if (side === 'a') {
    return {
      ...match,
      sideAIds: participantIds,
      sideAId: participantIds[0] ?? null,
    };
  }
  return {
    ...match,
    sideBIds: participantIds,
    sideBId: participantIds[0] ?? null,
  };
}

export function updatePreviewMatchParticipant(
  preview: DrawPreview,
  roundNumber: number,
  matchIndex: number,
  side: 'a' | 'b',
  participantId: string | null
): DrawPreview {
  const rounds = preview.rounds.map((round) => {
    if (round.roundNumber !== roundNumber) return round;
    const matches = round.matches.map((match) => {
      if (match.matchIndex !== matchIndex) return match;
      return updatePreviewMatchSideIds(match, side, participantId ? [participantId] : []);
    });
    return { ...round, matches };
  });

  return recomputePreviewSummary(
    { ...preview, rounds },
    eligibleParticipantIdsFromPreview(preview)
  );
}

export function removeParticipantFromPreviewMatch(
  preview: DrawPreview,
  roundNumber: number,
  matchIndex: number,
  participantId: string
): DrawPreview {
  const rounds = preview.rounds.map((round) => {
    if (round.roundNumber !== roundNumber) return round;
    const matches = round.matches.map((match) => {
      if (match.matchIndex !== matchIndex) return match;
      const sideAIds = getDrawPreviewSideIds(match, 'a').filter((id) => id !== participantId);
      const sideBIds = getDrawPreviewSideIds(match, 'b').filter((id) => id !== participantId);
      return updatePreviewMatchSideIds(
        updatePreviewMatchSideIds(match, 'a', sideAIds),
        'b',
        sideBIds
      );
    });
    return { ...round, matches };
  });

  return recomputePreviewSummary(
    { ...preview, rounds },
    eligibleParticipantIdsFromPreview(preview)
  );
}

function replaceParticipantOnPreviewSide(
  participantIds: string[],
  currentParticipantId: string,
  nextParticipantId: string
): string[] {
  const index = participantIds.indexOf(currentParticipantId);
  if (index < 0) return participantIds;
  const next = [...participantIds];
  next[index] = nextParticipantId;
  return next;
}

function assignParticipantToPreviewSideSlot(
  currentIds: string[],
  participantId: string,
  slotIndex: number,
  capacity: number
): string[] {
  if (capacity === 1) {
    return [participantId];
  }

  const primary = slotIndex === 0 ? participantId : currentIds[0];
  const partner = slotIndex === 1 ? participantId : currentIds[1];

  if (primary && partner) {
    return [primary, partner];
  }
  if (primary) {
    return [primary];
  }
  return [participantId];
}

export function swapPreviewParticipants(
  preview: DrawPreview,
  roundNumber: number,
  sourceParticipantId: string,
  targetParticipantId: string
): DrawPreview {
  let sourceSide: { matchIndex: number; side: 'a' | 'b' } | null = null;
  let targetSide: { matchIndex: number; side: 'a' | 'b' } | null = null;

  const round = preview.rounds.find((entry) => entry.roundNumber === roundNumber);
  if (!round) return preview;

  for (const match of round.matches) {
    if (getDrawPreviewSideIds(match, 'a').includes(sourceParticipantId)) {
      sourceSide = { matchIndex: match.matchIndex, side: 'a' };
    }
    if (getDrawPreviewSideIds(match, 'b').includes(sourceParticipantId)) {
      sourceSide = { matchIndex: match.matchIndex, side: 'b' };
    }
    if (getDrawPreviewSideIds(match, 'a').includes(targetParticipantId)) {
      targetSide = { matchIndex: match.matchIndex, side: 'a' };
    }
    if (getDrawPreviewSideIds(match, 'b').includes(targetParticipantId)) {
      targetSide = { matchIndex: match.matchIndex, side: 'b' };
    }
  }

  const sittingOut = new Set(round.sittingOut);
  const sourceInSittingOut = sittingOut.has(sourceParticipantId);
  const targetInSittingOut = sittingOut.has(targetParticipantId);

  let next = preview;

  if (sourceSide && targetSide) {
    const sourceMatch = round.matches.find((match) => match.matchIndex === sourceSide!.matchIndex);
    const targetMatch = round.matches.find((match) => match.matchIndex === targetSide!.matchIndex);
    if (sourceMatch && targetMatch) {
      const sourceIds = getDrawPreviewSideIds(sourceMatch, sourceSide.side);
      const targetIds = getDrawPreviewSideIds(targetMatch, targetSide.side);
      next = {
        ...next,
        rounds: next.rounds.map((entry) => {
          if (entry.roundNumber !== roundNumber) return entry;
          return {
            ...entry,
            matches: entry.matches.map((match) => {
              if (match.matchIndex === sourceSide!.matchIndex) {
                return updatePreviewMatchSideIds(
                  match,
                  sourceSide!.side,
                  replaceParticipantOnPreviewSide(sourceIds, sourceParticipantId, targetParticipantId)
                );
              }
              if (match.matchIndex === targetSide!.matchIndex) {
                return updatePreviewMatchSideIds(
                  match,
                  targetSide!.side,
                  replaceParticipantOnPreviewSide(targetIds, targetParticipantId, sourceParticipantId)
                );
              }
              return match;
            }),
          };
        }),
      };
    }
    return recomputePreviewSummary(next);
  }

  if (sourceSide && targetInSittingOut) {
    next = removeParticipantFromPreviewMatch(
      next,
      roundNumber,
      sourceSide.matchIndex,
      sourceParticipantId
    );
    const updatedSittingOut = round.sittingOut
      .filter((id) => id !== targetParticipantId)
      .concat(sourceParticipantId);
    next = updatePreviewRoundSittingOut(next, roundNumber, updatedSittingOut);
    return recomputePreviewSummary(next);
  }

  if (targetSide && sourceInSittingOut) {
    next = assignPreviewParticipantToSlot(
      next,
      roundNumber,
      targetSide.matchIndex,
      targetSide.side,
      sourceParticipantId
    );
    const updatedSittingOut = round.sittingOut
      .filter((id) => id !== sourceParticipantId)
      .concat(targetParticipantId);
    next = updatePreviewRoundSittingOut(next, roundNumber, updatedSittingOut);
    return recomputePreviewSummary(next);
  }

  return preview;
}

function updatePreviewRoundSittingOut(
  preview: DrawPreview,
  roundNumber: number,
  sittingOut: string[]
): DrawPreview {
  const rounds = preview.rounds.map((round) =>
    round.roundNumber === roundNumber ? { ...round, sittingOut } : round
  );
  return recomputePreviewSummary(
    { ...preview, rounds },
    eligibleParticipantIdsFromPreview(preview)
  );
}

export function getPreviewRound(
  preview: DrawPreview,
  roundNumber: number
): DrawPreviewRound | undefined {
  return preview.rounds.find((round) => round.roundNumber === roundNumber);
}

/** Participants not yet placed in a match or sitting-out list for the given round. */
export function getAvailableParticipantsForPreviewRound(
  preview: DrawPreview,
  roundNumber: number,
  eligibleParticipantIds: string[]
): string[] {
  const round = getPreviewRound(preview, roundNumber);
  if (!round) return eligibleParticipantIds;

  const assigned = new Set<string>();
  for (const match of round.matches) {
    for (const participantId of getDrawPreviewSideIds(match, 'a')) {
      assigned.add(participantId);
    }
    for (const participantId of getDrawPreviewSideIds(match, 'b')) {
      assigned.add(participantId);
    }
  }
  for (const id of round.sittingOut) {
    assigned.add(id);
  }

  return eligibleParticipantIds.filter((id) => !assigned.has(id));
}

export function assignPreviewParticipantToSlot(
  preview: DrawPreview,
  roundNumber: number,
  matchIndex: number,
  side: 'a' | 'b',
  participantId: string,
  slotIndex = 0,
  sideCapacity?: number
): DrawPreview {
  let next = preview;
  const round = getPreviewRound(next, roundNumber);
  const targetMatch = round?.matches.find((match) => match.matchIndex === matchIndex);
  const sourceMatch = round?.matches.find((match) => previewMatchIncludesParticipant(match, participantId));

  if (sourceMatch) {
    next = removeParticipantFromPreviewMatch(
      next,
      roundNumber,
      sourceMatch.matchIndex,
      participantId
    );
  } else if (round?.sittingOut.includes(participantId)) {
    next = {
      ...next,
      rounds: next.rounds.map((entry) =>
        entry.roundNumber === roundNumber
          ? { ...entry, sittingOut: entry.sittingOut.filter((id) => id !== participantId) }
          : entry
      ),
    };
  }

  if (!targetMatch) {
    return recomputePreviewSummary(next, eligibleParticipantIdsFromPreview(next));
  }

  const refreshedRound = getPreviewRound(next, roundNumber);
  const refreshedTargetMatch = refreshedRound?.matches.find((match) => match.matchIndex === matchIndex);
  if (!refreshedTargetMatch) {
    return recomputePreviewSummary(next, eligibleParticipantIdsFromPreview(next));
  }

  const currentIds = getDrawPreviewSideIds(refreshedTargetMatch, side);
  const capacity =
    sideCapacity ?? (isDoublesFormatPreviewMatch(refreshedTargetMatch) ? 2 : 1);
  const nextIds = assignParticipantToPreviewSideSlot(
    currentIds,
    participantId,
    slotIndex,
    capacity
  );

  next = {
    ...next,
    rounds: next.rounds.map((entry) => {
      if (entry.roundNumber !== roundNumber) return entry;
      return {
        ...entry,
        matches: entry.matches.map((match) =>
          match.matchIndex === matchIndex ? updatePreviewMatchSideIds(match, side, nextIds) : match
        ),
      };
    }),
  };

  return recomputePreviewSummary(next, eligibleParticipantIdsFromPreview(next));
}

function eligibleParticipantIdsFromPreview(preview: DrawPreview): string[] {
  const ids = new Set<string>(preview.unassignedParticipantIds);
  for (const round of preview.rounds) {
    for (const match of round.matches) {
      for (const participantId of getDrawPreviewSideIds(match, 'a')) {
        ids.add(participantId);
      }
      for (const participantId of getDrawPreviewSideIds(match, 'b')) {
        ids.add(participantId);
      }
    }
    for (const id of round.sittingOut) {
      ids.add(id);
    }
  }
  return [...ids];
}

function recomputePreviewSummary(
  preview: DrawPreview,
  eligibleParticipantIds?: string[]
): DrawPreview {
  const allParticipantIds = new Set<string>();
  for (const round of preview.rounds) {
    for (const match of round.matches) {
      for (const participantId of getDrawPreviewSideIds(match, 'a')) {
        allParticipantIds.add(participantId);
      }
      for (const participantId of getDrawPreviewSideIds(match, 'b')) {
        allParticipantIds.add(participantId);
      }
    }
    for (const id of round.sittingOut) {
      allParticipantIds.add(id);
    }
  }

  const totalMatches = preview.rounds.reduce((sum, round) => sum + round.matches.length, 0);
  const byeCount = preview.rounds.reduce(
    (sum, round) => sum + round.matches.filter((match) => match.isBye).length,
    0
  );

  const pool = eligibleParticipantIds ?? preview.unassignedParticipantIds;
  const unassignedParticipantIds = pool.filter((id) => !allParticipantIds.has(id));

  return {
    ...preview,
    totalMatches,
    byeCount,
    unassignedParticipantIds,
  };
}

export function getPreviewMatchSideParticipantIds(match: DrawPreviewMatch): {
  sideAIds: string[];
  sideBIds: string[];
} {
  return {
    sideAIds: getDrawPreviewSideIds(match, 'a'),
    sideBIds: getDrawPreviewSideIds(match, 'b'),
  };
}

export function buildPreviewDrawMatchListItems(input: {
  preview: DrawPreview;
  event: PlayEvent;
  participantNamesById: Record<string, string>;
  courtAssignmentsByKey?: Record<string, string | null | undefined>;
  roundNumber?: number;
}): DrawMatchListItem[] {
  const {
    preview,
    event,
    participantNamesById,
    courtAssignmentsByKey = {},
    roundNumber,
  } = input;
  const singleElimRoundCount = preview.rounds.length;
  const rounds =
    roundNumber != null
      ? preview.rounds.filter((round) => round.roundNumber === roundNumber)
      : preview.rounds;

  const items: DrawMatchListItem[] = [];

  for (const previewRound of rounds) {
    const sortedMatches = [...previewRound.matches].sort(
      (a, b) => a.matchIndex - b.matchIndex
    );

    for (const [indexInRound, match] of sortedMatches.entries()) {
      const assignmentKey = `${previewRound.roundNumber}-${indexInRound}`;
      const elimDisplay =
        event.format === 'single_elimination'
          ? getSingleEliminationMatchDisplay(
              previewRound.roundNumber,
              match.matchIndex,
              indexInRound,
              singleElimRoundCount,
              match,
              participantNamesById
            )
          : null;

      const { sideAIds, sideBIds } = getPreviewMatchSideParticipantIds(match);

      items.push({
        id: `preview-${assignmentKey}`,
        roundNumber: previewRound.roundNumber,
        matchIndex: indexInRound,
        sideAIds,
        sideBIds,
        courtId: courtAssignmentsByKey[assignmentKey] ?? null,
        result: match.isBye ? 'bye' : 'scheduled',
        isBye: match.isBye,
        matchLabel: elimDisplay?.matchLabel,
        sideAPlaceholder: elimDisplay?.sideAPlaceholder,
        sideBPlaceholder: elimDisplay?.sideBPlaceholder,
        sideAIsFeeder: elimDisplay?.sideAIsFeeder,
        sideBIsFeeder: elimDisplay?.sideBIsFeeder,
      });
    }
  }

  return items;
}

/** Roster participants not yet placed in a match for a live (persisted) round. */
export function getAvailableParticipantsForLiveRound(
  roster: PlayEventParticipant[],
  matches: Array<{
    roundNumber: number;
    sideAParticipantId?: string | null;
    sideBParticipantId?: string | null;
  }>,
  roundNumber: number,
  excludeParticipantIds: string[] = []
): PlayEventParticipant[] {
  const assigned = new Set<string>(excludeParticipantIds);
  for (const match of matches) {
    if (match.roundNumber !== roundNumber) continue;
    if (match.sideAParticipantId) assigned.add(match.sideAParticipantId);
    if (match.sideBParticipantId) assigned.add(match.sideBParticipantId);
  }
  return roster.filter((participant) => !assigned.has(participant.id));
}
